import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { BackgroundMode } from '@anuradev/capacitor-background-mode';
import { Song, SongRevision } from '@/types/song';
import audioPlayerService from '@/services/audio/player';
import { songsApi } from '@/services/api/songs';

interface MediaControlsPlugin {
  updateMetadata(options: { title: string; artist: string; artworkUrl?: string }): Promise<void>;
  updatePlaybackState(options: { isPlaying: boolean; position: number; duration: number }): Promise<void>;
  stop(): Promise<void>;
  onAction(callback: (data: { action: string }) => void): Promise<void>;
}

const MediaControls = Capacitor.isNativePlatform()
  ? registerPlugin<MediaControlsPlugin>('MediaControls')
  : null;

export interface QueueItem {
  song: Song;
  revision?: SongRevision;
}

/**
 * Build a queue item for a song, preferring its pinned revision (matching the
 * single-song play behaviour) so album playback plays the same audio the play
 * button would.
 */
function buildQueueItem(song: Song): QueueItem {
  if (song.pinnedRevisionId) {
    return {
      song,
      revision: {
        songRevisionId: song.pinnedRevisionId,
        songId: song.songId,
        songName: song.pinnedRevisionName || song.songName,
        isFavorite: false,
        pinned: true,
        revised: '',
      },
    };
  }
  return { song };
}

interface AudioPlayerContextType {
  currentSong: Song | null;
  currentRevision: SongRevision | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  queue: QueueItem[];
  currentIndex: number;
  playSong: (song: Song, revision?: SongRevision, usePinned?: boolean) => Promise<void>;
  playQueue: (items: QueueItem[], startIndex?: number) => Promise<void>;
  playSongs: (songs: Song[], startIndex?: number) => Promise<void>;
  togglePlayPause: () => void;
  playNext: () => Promise<void>;
  playPrevious: () => Promise<void>;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  stop: () => void;
  clearQueue: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export function AudioPlayerProvider({ children }: { children: ReactNode }) {
  const [currentSong, setCurrentSong] = useState<Song | null>(null);
  const [currentRevision, setCurrentRevision] = useState<SongRevision | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(audioPlayerService.getVolume());
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  async function playSong(song: Song, revision?: SongRevision, usePinned: boolean = false) {
    try {
      // Determine which revision to play
      let targetRevision = revision;

      // If usePinned is true and no specific revision is provided, use the
      // pinned revision id from the song list so we can play it without a
      // per-song details fetch. (The list endpoint is authoritative: no
      // pinnedRevisionId means the song has no pinned revision.)
      if (usePinned && !revision && song.pinnedRevisionId) {
        targetRevision = {
          songRevisionId: song.pinnedRevisionId,
          songId: song.songId,
          songName: song.pinnedRevisionName || song.songName,
          isFavorite: false,
          pinned: true,
          revised: '',
        };
      }

      const audioUrlResponse = await songsApi.getAudioUrl(song.songId, {
        revisionId: targetRevision?.songRevisionId,
      });

      await audioPlayerService.loadAudio(audioUrlResponse.audioUrl);
      await audioPlayerService.play();

      setCurrentSong(song);
      setCurrentRevision(targetRevision || null);
      setIsPlaying(true);
      // Clear queue when playing a single song
      setQueue([]);
      setCurrentIndex(-1);
    } catch (error) {
      console.error('Failed to play song:', error);
      throw error;
    }
  }

  // Play a list of songs as a queue (e.g. an album), preferring pinned revisions.
  async function playSongs(songs: Song[], startIndex: number = 0) {
    await playQueue(songs.map(buildQueueItem), startIndex);
  }

  async function playQueue(items: QueueItem[], startIndex: number = 0) {
    if (items.length === 0) return;

    setQueue(items);
    setCurrentIndex(startIndex);

    const item = items[startIndex];
    try {
      const audioUrlResponse = await songsApi.getAudioUrl(item.song.songId, {
        revisionId: item.revision?.songRevisionId,
      });

      await audioPlayerService.loadAudio(audioUrlResponse.audioUrl);
      await audioPlayerService.play();

      setCurrentSong(item.song);
      setCurrentRevision(item.revision || null);
      setIsPlaying(true);
    } catch (error) {
      console.error('Failed to play queue:', error);
      throw error;
    }
  }

  const playNext = useCallback(async () => {
    if (queue.length === 0 || currentIndex >= queue.length - 1) return;

    const nextIndex = currentIndex + 1;
    const item = queue[nextIndex];

    try {
      const audioUrlResponse = await songsApi.getAudioUrl(item.song.songId, {
        revisionId: item.revision?.songRevisionId,
      });

      await audioPlayerService.loadAudio(audioUrlResponse.audioUrl);
      await audioPlayerService.play();

      setCurrentSong(item.song);
      setCurrentRevision(item.revision || null);
      setCurrentIndex(nextIndex);
      setIsPlaying(true);
    } catch (error) {
      console.error('Failed to play next song:', error);
    }
  }, [queue, currentIndex]);

  async function playPrevious() {
    if (queue.length === 0 || currentIndex <= 0) return;

    const prevIndex = currentIndex - 1;
    const item = queue[prevIndex];

    try {
      const audioUrlResponse = await songsApi.getAudioUrl(item.song.songId, {
        revisionId: item.revision?.songRevisionId,
      });

      await audioPlayerService.loadAudio(audioUrlResponse.audioUrl);
      await audioPlayerService.play();

      setCurrentSong(item.song);
      setCurrentRevision(item.revision || null);
      setCurrentIndex(prevIndex);
      setIsPlaying(true);
    } catch (error) {
      console.error('Failed to play previous song:', error);
    }
  }

  function clearQueue() {
    setQueue([]);
    setCurrentIndex(-1);
  }

  useEffect(() => {
    const handleTimeUpdate = () => {
      setCurrentTime(audioPlayerService.getCurrentTime());
      setDuration(audioPlayerService.getDuration());
    };

    const handleEnded = async () => {
      setIsPlaying(false);
      // Auto-play next track in queue
      if (currentIndex >= 0 && currentIndex < queue.length - 1) {
        await playNext();
      }
    };

    audioPlayerService.addEventListener('timeupdate', handleTimeUpdate);
    audioPlayerService.addEventListener('ended', handleEnded);

    return () => {
      audioPlayerService.removeEventListener('timeupdate', handleTimeUpdate);
      audioPlayerService.removeEventListener('ended', handleEnded);
    };
  }, [currentIndex, queue, playNext]);

  function togglePlayPause() {
    if (audioPlayerService.isPlaying()) {
      audioPlayerService.pause();
      setIsPlaying(false);
    } else {
      audioPlayerService.play();
      setIsPlaying(true);
    }
  }

  function seek(time: number) {
    audioPlayerService.seek(time);
    setCurrentTime(time);
  }

  function setVolume(vol: number) {
    audioPlayerService.setVolume(vol);
    setVolumeState(vol);
  }

  function stop() {
    audioPlayerService.stop();
    setIsPlaying(false);
    setCurrentTime(0);
  }

  // Enable/disable background mode to keep audio alive when app is backgrounded
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    if (isPlaying) {
      BackgroundMode.enable({
        title: 'SongKit',
        text: currentRevision?.songName || currentSong?.songName || 'Playing audio',
      });
    } else {
      BackgroundMode.disable();
    }
  }, [isPlaying]);

  // Keep refs to the latest queue-aware callbacks so the once-registered media
  // control handlers (native + web) always operate on the current queue rather
  // than a stale closure. This is what makes car/Bluetooth prev/next work after
  // an album queue is loaded.
  const playNextRef = useRef(playNext);
  const playPreviousRef = useRef(playPrevious);
  useEffect(() => {
    playNextRef.current = playNext;
    playPreviousRef.current = playPrevious;
  });

  // Native media controls (Android/iOS) — register action listener once
  const actionListenerRegistered = useRef(false);
  useEffect(() => {
    if (!MediaControls || actionListenerRegistered.current) return;
    actionListenerRegistered.current = true;

    MediaControls.onAction((data: { action: string }) => {
      const action = data.action;
      if (action === 'play') {
        audioPlayerService.play();
        setIsPlaying(true);
      } else if (action === 'pause') {
        audioPlayerService.pause();
        setIsPlaying(false);
      } else if (action === 'nexttrack') {
        playNextRef.current();
      } else if (action === 'previoustrack') {
        playPreviousRef.current();
      } else if (action.startsWith('seekto:')) {
        const ms = parseInt(action.split(':')[1], 10);
        const secs = ms / 1000;
        audioPlayerService.seek(secs);
        setCurrentTime(secs);
      }
    });
  }, []);

  // Web Media Session controls (PWA / browser / some WebView setups). Native
  // platforms use the MediaControls plugin above instead.
  useEffect(() => {
    if (MediaControls || !('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    ms.setActionHandler('play', () => {
      audioPlayerService.play();
      setIsPlaying(true);
    });
    ms.setActionHandler('pause', () => {
      audioPlayerService.pause();
      setIsPlaying(false);
    });
    ms.setActionHandler('nexttrack', () => playNextRef.current());
    ms.setActionHandler('previoustrack', () => playPreviousRef.current());
    return () => {
      ms.setActionHandler('play', null);
      ms.setActionHandler('pause', null);
      ms.setActionHandler('nexttrack', null);
      ms.setActionHandler('previoustrack', null);
    };
  }, []);

  // Sync metadata to native media controls or web Media Session
  useEffect(() => {
    if (!currentSong) {
      if (MediaControls) MediaControls.stop();
      if ('mediaSession' in navigator) navigator.mediaSession.metadata = null;
      return;
    }

    const title = currentRevision?.songName || currentSong.songName;
    const artist = currentSong.songName !== title ? currentSong.songName : '';
    const artworkUrl = currentRevision?.coverArtUrl || currentSong.coverArtUrl;

    if (MediaControls) {
      MediaControls.updateMetadata({ title, artist, artworkUrl: artworkUrl || '' });
    } else if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title,
        artist,
        ...(artworkUrl ? { artwork: [{ src: artworkUrl, sizes: '512x512', type: 'image/png' }] } : {}),
      });
    }
  }, [currentSong, currentRevision]);

  // Sync playback state to native media controls or web Media Session
  useEffect(() => {
    if (MediaControls) {
      MediaControls.updatePlaybackState({
        isPlaying,
        position: currentTime,
        duration,
      });
    } else if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
      if (duration) {
        navigator.mediaSession.setPositionState({
          duration,
          playbackRate: 1,
          position: Math.min(currentTime, duration),
        });
      }
    }
  }, [isPlaying, currentTime, duration]);

  const value = {
    currentSong,
    currentRevision,
    isPlaying,
    currentTime,
    duration,
    volume,
    queue,
    currentIndex,
    playSong,
    playQueue,
    playSongs,
    togglePlayPause,
    playNext,
    playPrevious,
    seek,
    setVolume,
    stop,
    clearQueue,
  };

  return <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>;
}

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);
  if (context === undefined) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }
  return context;
}
